import { useState, useEffect } from 'react';
import { Modal, Button } from '@plick/ui';
import { IconArrowRightCircleFilled, IconMessage2Exclamation } from '@tabler/icons-react';
import { openExternalUrl } from '@utils/navigationExternalUrl';
import { EXTERNAL_URLS } from '@utils/constants';
import { plickTheme } from '@theme/index';
import useTracking, { TRACK_EVENT_NAMES } from '@utils/hooks/useTracking';
import type { FounderContactModalProps } from '@interfaces/Feedback';

const FounderContactModal = ({ onClose, onContinue }: FounderContactModalProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const { trackEvent } = useTracking();

  useEffect(() => {
    // Track modal opened
    trackEvent(TRACK_EVENT_NAMES.FeedbackWarningModalOpened);
  }, [trackEvent]);

  const handleClose = () => {
    trackEvent(TRACK_EVENT_NAMES.FeedbackWarningModalClosed);
    setIsOpen(false);
    setTimeout(onClose, 300); // Wait for animation
  };

  const handleContinue = () => {
    trackEvent(TRACK_EVENT_NAMES.FeedbackWarningModalContinued);
    setIsOpen(false);
    setTimeout(onContinue, 300); // Wait for animation
  };

  return (
    // @ts-expect-error - maxWidth and maxHeight are supported by the underlying component but not in the library types
    <Modal padding="0" maxHeight="90vh" maxWidth="42rem" isOpen={isOpen} onClose={handleClose}>
      <div className="p-4 md:p-6">
        <h2 className="text-lg md:text-xl font-bold text-color-primary mb-3 md:mb-4">Ayúdanos a Mejorar</h2>

        <div className="flex mb-3 md:mb-4">
          <div className="mr-2 md:mr-3 text-color-primary">
            <IconMessage2Exclamation stroke={2} className="text-xl md:text-2xl" />
          </div>
          <div>
            <p className="mb-3 text-sm md:text-base">Estás por conectar con el equipo de Plick.</p>
            <p className="mb-2 text-sm md:text-base font-medium">Cuéntanos:</p>
            <ul className="mb-3 md:mb-4 text-sm md:text-base list-disc pl-4 md:pl-5 space-y-1.5 md:space-y-1">
              <li>¿Qué necesitas hacer que Plick no te deja?</li>
              <li>¿Qué te frustra o te toma mucho tiempo?</li>
              <li>¿Qué ha mejorado en tu operación con Plick?</li>
              <li>Ideas para hacer tu trabajo más fácil</li>
            </ul>
            <p className="mb-3 md:mb-4 text-sm md:text-base">Tu opinión construye el futuro de Plick 🚀</p>
          </div>
        </div>

        <div className="flex items-center mb-3 md:mb-4 text-xs md:text-sm text-center text-color-primary">
          <span>Para soporte técnico, clic aquí </span>
          <IconArrowRightCircleFilled
            width={24}
            height={24}
            className="md:w-8 md:h-8"
            color={plickTheme.colors.success}
          />
          <button
            onClick={() => openExternalUrl(EXTERNAL_URLS.SUPPORT_WHATSAPP)}
            className="text-color-primary underline hover:text-color-whatsapp_dark transition-colors"
          >
            WhatsApp
          </button>
        </div>

        <div className="flex flex-col md:flex-row justify-end gap-2">
          <Button.Tertiary text="REGRESAR" onClick={handleClose} />
          <Button.Primary text="CONTINUAR" onClick={handleContinue} />
        </div>
      </div>
    </Modal>
  );
};

export default FounderContactModal;
